import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardStackedBarChart from '@components/pages/Dashboard/MetricCharts/DashboardStackedBarChart.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import type { UseClaimsResult } from '@hooks/dashboard/useClaims'
import { CLAIMS_CHART_COLORS } from '@constants/Dashboard/financeDashboard.constants'
import {
  FinanceClaimsLegend,
  FinanceMetricTilesRow,
} from '../../FinanceMetricTiles.component'
import {
  formatChartCurrencyKUnits,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'

type ClaimsCardProps = {
  titleHref: string
  claims: UseClaimsResult
}

export function ClaimsCard({
  titleHref,
  claims,
}: ClaimsCardProps): React.ReactElement {
  const {
    loading,
    underReviewStr,
    paidStr,
    pendingStr,
    deniedStr,
    chart,
    hasData,
    hasPayload,
  } = claims
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <DashboardSectionCard
      title="Claims"
      titleHref={titleHref}
      className="dashboard-finance-claims-card"
      loading={pending}
      skeleton={{ metrics: 4, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-finance-treatment-value">
        <FinanceMetricTilesRow
          columns={4}
          metrics={[
            {
              id: 'under-review',
              leftLabel: 'Under review',
              value: loading ? HYGIENE_PLACEHOLDER : underReviewStr,
            },
            {
              id: 'paid',
              leftLabel: 'Paid',
              value: loading ? HYGIENE_PLACEHOLDER : paidStr,
            },
            {
              id: 'pending',
              leftLabel: 'Pending',
              value: loading ? HYGIENE_PLACEHOLDER : pendingStr,
            },
            {
              id: 'denied',
              leftLabel: 'Denied',
              value: loading ? HYGIENE_PLACEHOLDER : deniedStr,
            },
          ]}
        />
        {isEmpty ? (
          <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
        ) : (
          <div className="dashboard-finance-treatment-value-chart-wrap">
            <DashboardStackedBarChart
              data={loading ? [] : chart.data}
              series={[{ dataKey: 'value', name: '', color: '' }]}
              horizontal={false}
              stacked={false}
              categoryIndicatorColors={CLAIMS_CHART_COLORS}
              hideCategoryIndicatorCircles
              valueFormatter={formatChartCurrencyKUnits}
              height={220}
              yAxisDomain={chart.yDomain}
              yAxisTicks={chart.yTicks}
              showLegend={false}
              className="dashboard-finance-treatment-value-chart"
            />
            <FinanceClaimsLegend />
          </div>
        )}
      </div>
    </DashboardSectionCard>
  )
}
